import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { X, Trash2, Download, RefreshCw, AlertCircle } from 'lucide-react';
import { getSystemLogs, deleteSystemLog, deleteAllSystemLogs, SystemLog } from '../services/logService';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface SystemLogsViewerProps {
  onClose: () => void;
}

export const SystemLogsViewer: React.FC<SystemLogsViewerProps> = ({ onClose }) => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: 'single' | 'all', id?: string } | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedLogs = await getSystemLogs(100);
      setLogs(fetchedLogs);
    } catch (err: any) {
      setError('Error al cargar los logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const executeDelete = async () => {
    if (!confirmAction) return;
    
    if (confirmAction.type === 'single' && confirmAction.id) {
      try {
        await deleteSystemLog(confirmAction.id);
        setLogs(logs.filter(l => l.id !== confirmAction.id));
      } catch (err: any) {
        setError('Error al eliminar: ' + err.message);
      }
    } else if (confirmAction.type === 'all') {
      setDeleting(true);
      try {
        await deleteAllSystemLogs(logs);
        setLogs([]);
      } catch (err: any) {
        setError('Error al eliminar todos los logs: ' + err.message);
      } finally {
        setDeleting(false);
      }
    }
    setConfirmAction(null);
  };

  const handleDownload = () => {
    const csvContent = [
      ['ID', 'Fecha', 'Operación', 'Error', 'Input', 'Stack', 'User ID'].join(','),
      ...logs.map(log => {
        const safeInput = log.input ? `"${log.input.replace(/"/g, '""')}"` : '';
        const safeError = `"${log.errorMessage.replace(/"/g, '""')}"`;
        const safeStack = log.errorStack ? `"${log.errorStack.replace(/"/g, '""')}"` : '';
        return `${log.id},${log.timestamp},${log.operation},${safeError},${safeInput},${safeStack},${log.userId || ''}`;
      })
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `system_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-800 w-full max-w-5xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="p-6 border-b border-zinc-800 bg-zinc-800/30 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <AlertCircle size={20} className="text-red-400" />
              Logs del Sistema (Gemini API)
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Mostrando los últimos {logs.length} errores. Solo visible para Super Admin.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogs}
              className="p-2 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
              title="Actualizar"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-4 bg-zinc-900/50 border-b border-zinc-800 flex justify-between items-center">
          <button
            onClick={handleDownload}
            disabled={logs.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors"
          >
            <Download size={16} />
            Exportar CSV
          </button>
          <button
            onClick={() => setConfirmAction({ type: 'all' })}
            disabled={logs.length === 0 || deleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-sm font-medium transition-colors border border-red-500/20"
          >
            <Trash2 size={16} />
            {deleting ? 'Eliminando...' : 'Depurar Todos'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          {loading ? (
            <div className="text-center py-10 text-zinc-500">Cargando logs...</div>
          ) : error ? (
            <div className="text-center py-10 text-red-400">{error}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">No hay errores registrados.</div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 relative group">
                  <button
                    onClick={() => log.id && setConfirmAction({ type: 'single', id: log.id })}
                    className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                    title="Eliminar este log"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs font-bold rounded-md border border-red-500/20">
                      {log.operation}
                    </span>
                    <span className="text-xs text-zinc-500">
                      {format(new Date(log.timestamp), "dd MMM yyyy, HH:mm:ss", { locale: es })}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-red-300 mb-2">
                    {log.errorMessage}
                  </div>
                  {log.input && (
                    <div className="mt-3">
                      <div className="text-xs font-bold text-zinc-500 mb-1">Input:</div>
                      <pre className="bg-zinc-900 p-3 rounded-lg text-xs text-zinc-300 overflow-x-auto whitespace-pre-wrap border border-zinc-800">
                        {log.input}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-zinc-900 border border-zinc-800 p-6 rounded-2xl w-full max-w-sm shadow-2xl"
          >
            <h3 className="text-lg font-bold text-white mb-2">Confirmar eliminación</h3>
            <p className="text-zinc-400 text-sm mb-6">
              {confirmAction.type === 'all' 
                ? '¿Estás seguro de eliminar TODOS los logs? Esta acción no se puede deshacer.' 
                : '¿Estás seguro de eliminar este log?'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeDelete}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Eliminar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};
